import React, { useEffect, useState, useContext, useRef } from "react";
import { Box, Typography, Card, CardContent, Divider, Button, Chip } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { AppContext } from "../context/AppContext";

const green = "#0A3D91";
const red = "#C62828";
const gray = "#757575";
const fsBody = "1rem";
const fsSub = "1.25rem";

function getCSRFToken() {
  const m = document.cookie.match(/(^|;\s*)csrftoken=([^;]+)/);
  return m ? decodeURIComponent(m[2]) : "";
}

function statusColor(s) {
  const x = String(s || "").toLowerCase();
  if (x === "approved" || x === "completed") return green;
  if (x === "canceled" || x === "rejected" || x === "failed") return red;
  return gray;
}

function statusLabel(s) {
  const x = String(s || "").toLowerCase();
  if (x === "approved" || x === "completed") return "Aprobado";
  if (x === "pending") return "Pendiente";
  if (x === "rejected") return "Rechazado";
  if (x === "failed") return "Fallido";
  if (x === "canceled") return "Cancelado";
  return "Pendiente";
}

export default function CheckoutSuccess() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { clearCart } = useContext(AppContext);

  const [order, setOrder] = useState(null);
  const clearedRef = useRef(false);

  const params = new URLSearchParams(search);
  const gateway = params.get("gateway");
  const orderIdParam = params.get("order_id");
  const ppId = params.get("id");
  const ppClientId = params.get("clientTransactionId");

  useEffect(() => {
    const load = async () => {
      const saved = (() => {
        try { return JSON.parse(localStorage.getItem("lastOrder") || "{}"); }
        catch { return {}; }
      })();

      if (gateway === "paypal") {
        const resp = await fetch(
          `/ecommerce/api/paypal/verify/?order_id=${encodeURIComponent(orderIdParam)}`,
          { credentials: "include" }
        );

        const data = await resp.json();

        const merged = {
          gateway: "paypal",
          order_id: data.order_id,
          status: data.status,
          amount: data.amount,
          items: data.items || [],
          payer: {
            email: data.payer_email || "",
            name: data.details?.payer?.name?.given_name || ""
          }
        };

        setOrder(merged);
        if (!clearedRef.current) {
          clearCart();
          clearedRef.current = true;
        }
        return;
      }

      if (!gateway && ppId && ppClientId) {
        const resp = await fetch("/ecommerce/api/payphone/confirm/", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRFToken": getCSRFToken() },
          body: JSON.stringify({ id: Number(ppId), clientTxId: ppClientId })
        });

        const data = await resp.json();
        const info = data.data || {};

        const merged = {
          gateway: "payphone",
          order_id: info.transactionId || ppId,
          status: data.status,
          amount: info.amount ? (info.amount / 100).toFixed(2) : "0.00",
          items: info.items || [],
          payer: {}
        };

        setOrder(merged);
        if (!clearedRef.current) {
          clearCart();
          clearedRef.current = true;
        }
        return;
      }

      if (saved && saved.items) {
        setOrder(saved);
        if (!clearedRef.current) {
          clearCart();
          clearedRef.current = true;
        }
      }
    };

    load();
  }, []);

  if (!order)
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography sx={{ fontSize: fsBody }}>Cargando detalle del pago...</Typography>
      </Box>
    );

  const color = statusColor(order.status);
  const readable = statusLabel(order.status);

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: { xs: 2, md: 4 } }}>
      <Typography sx={{ fontSize: "2rem", fontWeight: "bold", mb: 2 }}>
        Resultado del pago
      </Typography>

      <Card sx={{ borderLeft: `5px solid ${color}`, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Typography sx={{ fontSize: fsSub, fontWeight: "bold" }}>Estado:</Typography>
            <Chip label={readable} sx={{ backgroundColor: color, color: "#fff", fontWeight: 600 }} />
          </Box>

          <Typography sx={{ fontSize: fsBody }}>
            ID de la orden: <b>{order.order_id}</b>
          </Typography>

          <Typography sx={{ fontSize: fsBody }}>
            Total pagado: <b>US$ {order.amount}</b>
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{ fontSize: fsSub, fontWeight: "bold", mb: 2 }}>
            Productos comprados
          </Typography>

          {order.items.length === 0 ? (
            <Typography>No se encontraron productos.</Typography>
          ) : (
            order.items.map((item, idx) => (
              <Box key={idx} sx={{ mb: 2, p: 1, borderBottom: "1px solid #ddd" }}>
                <Typography sx={{ fontSize: fsBody, fontWeight: "bold" }}>
                  {item.quantity}× {item.name}
                </Typography>

                {item.brand && (
                  <Typography sx={{ fontSize: fsBody }}>Marca: {item.brand}</Typography>
                )}

                {item.reference && (
                  <Typography sx={{ fontSize: fsBody }}>Referencia: {item.reference}</Typography>
                )}

                {item.sku && (
                  <Typography sx={{ fontSize: fsBody }}>SKU: {item.sku}</Typography>
                )}

                <Typography sx={{ fontSize: fsBody }}>
                  Precio unitario: US$ {Number(item.price).toFixed(2)}
                </Typography>

                <Typography sx={{ fontSize: fsBody }}>
                  Subtotal: US$ {(Number(item.price) * item.quantity).toFixed(2)}
                </Typography>
              </Box>
            ))
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
        <Button
          variant="contained"
          sx={{ backgroundColor: green, px: 3 }}
          onClick={() => navigate("/categorias")}
        >
          Seguir comprando
        </Button>

        <Button
          variant="outlined"
          sx={{ borderColor: green, color: green }}
          onClick={() => navigate("/")}
        >
          Ir al inicio
        </Button>
      </Box>
    </Box>
  );
}
